<script lang="ts" setup>
import { ref } from "vue";
import DataView from "@/components/DataView.vue";
import type { Config } from "datatables.net";

interface Props {
  product: any;
  productStatus: any;
}

defineProps<Props>();

const timeRange = ref("30d");
const metrics = ref({
  totalRuns: 1247,
  successfulRuns: 1235,
  failedRuns: 12,
  successRate: 99.1,
  avgDuration: 4.2,
  totalRevenue: 2847,
});

const chartData = ref([
  { date: "2024-07-01", runs: 42, success: 41, revenue: 98 },
  { date: "2024-07-02", runs: 38, success: 37, revenue: 89 },
  { date: "2024-07-03", runs: 45, success: 44, revenue: 105 },
  { date: "2024-07-04", runs: 51, success: 50, revenue: 119 },
  { date: "2024-07-05", runs: 39, success: 38, revenue: 91 },
]);

const topErrors = ref([
  {
    id: "1",
    message: "Email service timeout",
    count: 8,
    percentage: 66.7,
    lastOccurred: "2 hours ago",
  },
  {
    id: "2",
    message: "Invalid email address format",
    count: 3,
    percentage: 25.0,
    lastOccurred: "1 day ago",
  },
  {
    id: "3",
    message: "CRM connection failed",
    count: 1,
    percentage: 8.3,
    lastOccurred: "3 days ago",
  },
]);

const performanceBySubscriber = ref([
  { name: "TechCorp Solutions", runs: 47, success: 46, rate: 97.9 },
  { name: "Digital Dynamics", runs: 23, success: 23, rate: 100.0 },
  { name: "Innovation Inc", runs: 12, success: 11, rate: 91.7 },
]);

const tableOptions = {
  columns: [
    { data: 'name', title: 'Subscriber' },
    { data: 'runs', title: 'Total Runs' },
    { data: 'success', title: 'Successful' },
    { data: 'rate', title: 'Success Rate', render: (d: number) => `<span class="badge ${d >= 95 ? 'bg-success' : d >= 90 ? 'bg-warning' : 'bg-danger'}">${d}%</span>` },
    { data: null, title: 'Trend', defaultContent: '<i class="bi bi-graph-up text-success"></i>' },
  ],
  layout: {
    topEnd: '',
    bottomEnd: '',
    bottomStart: '',
  },
} as Config;

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}
</script>

<template>
  <div class="analytics-tab">
    <!-- Coming Soon Banner — the metrics, charts, and tables below are demo data with no real
         backing yet. The page is left visible so the layout/intent is clear; everything is greyed
         out + pointer-disabled so publishers don't act on the numbers. -->
    <div class="alert alert-info d-flex align-items-center mb-4" role="alert">
      <i class="bi bi-info-circle me-2"></i>
      <span>Analytics are coming soon. The data shown below is illustrative only.</span>
    </div>

    <div class="coming-soon">
    <!-- Time Range Selector -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="card-title mb-0">Analytics Overview</h2>
      <select class="form-select w-auto" v-model="timeRange" disabled>
        <option value="7d">Last 7 days</option>
        <option value="30d">Last 30 days</option>
        <option value="90d">Last 90 days</option>
        <option value="1y">Last year</option>
      </select>
    </div>

    <!-- Key Metrics -->
    <div class="row g-3 mb-4">
      <div class="col-4 col-md-2">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-primary mb-2">
              {{ metrics.totalRuns.toLocaleString() }}
            </h3>
            <p class="text-muted small mb-2">Total Runs</p>
          </div>
        </div>
      </div>
      <div class="col-4 col-md-2">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-success mb-2">
              {{ metrics.successfulRuns.toLocaleString() }}
            </h3>
            <p class="text-muted small mb-2">Successful</p>
          </div>
        </div>
      </div>
      <div class="col-4 col-md-2">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-danger mb-2">{{ metrics.failedRuns }}</h3>
            <p class="text-muted small mb-2">Failed</p>
          </div>
        </div>
      </div>
      <div class="col-4 col-md-2">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-info mb-2">{{ metrics.successRate }}%</h3>
            <p class="text-muted small mb-2">Success Rate</p>
          </div>
        </div>
      </div>
      <div class="col-4 col-md-2">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-warning mb-2">{{ metrics.avgDuration }}m</h3>
            <p class="text-muted small mb-2">Avg Duration</p>
          </div>
        </div>
      </div>
      <div class="col-4 col-md-2">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-success mb-2">
              ${{ metrics.totalRevenue.toLocaleString() }}
            </h3>
            <p class="text-muted small mb-2">Revenue</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row g-4 mb-4">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title mb-3">Usage Trends</h2>
            <!-- Placeholder for chart -->
            <div class="chart-placeholder">
              <div
                class="d-flex justify-content-between align-items-end"
                style="height: 200px"
              >
                <div
                  v-for="(data, index) in chartData"
                  :key="index"
                  class="chart-bar d-flex flex-column align-items-center justify-content-end"
                  :style="{ height: (data.runs / 60) * 100 + '%' }"
                >
                  <div class="chart-bar-inner bg-primary"></div>
                  <div class="chart-label">{{ formatDate(data.date) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title mb-3">Top Errors</h2>
            <div class="error-list d-flex flex-column gap-3">
              <div
                v-for="error in topErrors"
                :key="error.id"
                class="error-item"
              >
                <div
                  class="d-flex justify-content-between align-items-start mb-1"
                >
                  <span class="fw-medium">{{ error.message }}</span>
                  <span class="badge bg-danger">{{ error.count }}</span>
                </div>
                <div class="d-flex justify-content-between text-muted small">
                  <span>{{ error.percentage }}% of errors</span>
                  <span>{{ error.lastOccurred }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Performance by Subscriber -->
    <h2 class="card-title mb-3">Performance by Subscriber</h2>
    <DataView
      :data="performanceBySubscriber"
      view-mode="table"
      :options="tableOptions"
    >
      <template #grid-card="{ row: subscriber }">
        <div class="card h-100 card-hover">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <h6 class="mb-0 fw-medium">{{ subscriber.name }}</h6>
              <span class="badge" :class="subscriber.rate >= 95 ? 'bg-success' : subscriber.rate >= 90 ? 'bg-warning' : 'bg-danger'">
                {{ subscriber.rate }}%
              </span>
            </div>
            <div class="small text-muted mb-2">
              <div><i class="bi bi-play-circle me-1"></i>{{ subscriber.runs }} total runs</div>
              <div><i class="bi bi-check-circle me-1"></i>{{ subscriber.success }} successful</div>
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <span class="text-muted small">Performance Trend</span>
              <i class="bi bi-graph-up text-success"></i>
            </div>
          </div>
        </div>
      </template>
      <template #empty-state>
        <div class="text-center py-5">
          <i class="bi bi-graph-up display-1 text-muted"></i>
          <h3 class="mt-3 text-muted">No subscriber data available</h3>
        </div>
      </template>
    </DataView>
    </div>
  </div>
</template>

<style scoped lang="scss">
.chart-placeholder {
  padding: 20px;
}

.chart-bar {
  flex: 1;
  margin: 0 2px;
}

.chart-bar-inner {
  width: 20px;
  border-radius: 2px 2px 0 0;
  margin-bottom: 8px;
}

.chart-label {
  font-size: 12px;
  color: $secondary;
}


.error-item {
  padding-bottom: 16px;
  border-bottom: 1px solid $border-color;
}

.error-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

/* COMMENTED OUT: Redundant - Bootstrap already handles responsive grid behavior */
/* Force column widths to ensure proper layout - 3 per row on mobile */
/*@media (max-width: 767.98px) {*/
/*  .row .col-4 {*/
/*    flex: 0 0 33.333333%;*/
/*    max-width: 33.333333%;*/
/*  }*/
/*}*/

/*@media (min-width: 768px) {*/
/*  .row .col-md-2 {*/
/*    flex: 0 0 16.666667%;*/
/*    max-width: 16.666667%;*/
/*  }*/
/*}*/

</style>
